// Управляющие элементы: кнопки, чипы, поля, тогл, чекбокс. SMY-66

import { useEffect, useLayoutEffect, useRef, type ButtonHTMLAttributes, type ComponentProps, type ReactNode } from 'react'
import { FILE_TYPES } from '@smyslika/core'
import { COMPOSER_MAX_HEIGHT, WEB } from '@smyslika/ui-tokens'

import { Icon } from './Icon.tsx'

/** Что принимает диалог выбора файла. Собран из тех же спецификаций, что и проверка. */
const ACCEPT = Object.values(FILE_TYPES)
  .flatMap((t) => [...t.mimeTypes, ...t.extensions])
  .join(',')

type ButtonKind = 'primary' | 'accent' | 'ghost' | 'danger'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  kind?: ButtonKind
  /** Растянуть на всю ширину — так основная кнопка стоит на 01, 04, 07, 08. */
  wide?: boolean
}

export function Button({ kind = 'primary', wide, className, children, ...rest }: ButtonProps) {
  return (
    <button
      type="button"
      className={`btn btn--${kind}${className ? ` ${className}` : ''}`}
      style={wide ? { width: '100%' } : undefined}
      {...rest}
    >
      {children}
    </button>
  )
}

/**
 * Круглая кнопка отправки 40px — экран 03б.
 *
 * `busy` — вопрос ушёл и разбирается (SMY-354): кнопка закрыта для второго
 * нажатия, но не бледная — бледной она бывает при пустом поле, и человек читал
 * её «нажатие не сработало». Имя для программы чтения — прежнее действие, а о
 * занятости говорит `aria-busy`.
 */
export function SendButton({ busy, className, ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { busy?: boolean }) {
  return (
    <button
      type="button"
      className={`btn btn--send${busy === true ? ' btn--busy' : ''}${className ? ` ${className}` : ''}`}
      aria-label="Отправить"
      aria-busy={busy === true}
      {...props}
      {...(busy === true ? { disabled: true } : {})}
    >
      {busy === true ? <span className="btn__spinner" aria-hidden="true" /> : <Icon name="send" size={20} />}
    </button>
  )
}

export interface ChipProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onSelect'> {
  selected?: boolean
  children: ReactNode
}

/**
 * Чип. Выбранность — `aria-pressed`, а не класс: программе чтения с экрана
 * нужно то же состояние, что и глазу, а рисуется оно из того же атрибута.
 */
export function Chip({ selected = false, children, className, ...rest }: ChipProps) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      className={`chip${className ? ` ${className}` : ''}`}
      {...rest}
    >
      {children}
    </button>
  )
}

export interface FieldProps extends ComponentProps<'input'> {
  /** Прямоугольное поле входа (радиус 16) вместо пилюли чата (26). */
  boxy?: boolean
}

export function Field({ boxy, className, value, ...rest }: FieldProps) {
  // С набранным текстом поле подсвечивается акцентом — правило экрана 03б.
  const filled = typeof value === 'string' && value !== ''
  return (
    <input
      className={`field${boxy ? ' field--boxy' : ''}${filled ? ' field--active' : ''}${className ? ` ${className}` : ''}`}
      value={value}
      {...rest}
    />
  )
}

/**
 * Поле ввода с иконками — нижняя строка экранов 02, 03, 06.
 *
 * Скрепка появляется, только когда экрану есть куда деть файл. Микрофона из
 * макета тут нет вовсе — распознавания речи в MVP не заведено, и кнопка
 * обещала бы то, чего нет.
 *
 * ⚠️ СКРЕПКА СТОИТ ВНУТРИ ПОЛЯ, а не рядом с ним. Рядом она была третьим
 * предметом в ряду и укорачивала поле на свои сорок точек; внутри она
 * читается как часть поля — тем, чем и является: способом положить файл в это
 * сообщение, а не отдельным действием экрана.
 *
 * Место под неё держит ОТСТУП ТЕКСТА (`--composer-pad-left`), а не колонка в
 * раскладке: кнопка вынута из потока и стоит поверх поля слева. Отсюда и
 * коробка `composer__box` — скрепке нужен родитель, от которого считать
 * положение.
 *
 * ⚠️ ПОЛЕ МНОГОСТРОЧНОЕ (SMY-360, 05.10.2026). До этого здесь стоял `<input>`:
 * вопрос в три сотни знаков был виден одним хвостом, а ⇧Enter, обещанный
 * подсказкой и комментарием ниже, строку не переносил — переносить в `<input>`
 * некуда. Теперь `<textarea>` растёт под текст до `COMPOSER_MAX_HEIGHT` —
 * того же числа, что у поля приложения, — и дальше прокручивается. `Field`
 * не тронут: это поле форм входа, и многострочность ему не нужна.
 */
export function Composer({
  value,
  onChange,
  onSend,
  onAttach,
  placeholder,
  disabled,
  busy,
  label,
  focusKey,
  sendBlocked,
}: {
  value: string
  onChange: (value: string) => void
  onSend?: () => void
  /** Выбран файл. Отсутствует — скрепки нет. */
  onAttach?: (file: File) => void
  placeholder?: string
  disabled?: boolean
  /**
   * Отправка закрыта, а поле правится: запрос длиннее предела (SMY-619). Не
   * `disabled` — тот закрыл бы и поле, и сократить написанное было бы негде.
   */
  sendBlocked?: boolean
  /**
   * Вопрос ушёл или файл грузится (SMY-354). Поле только для чтения, но не
   * бледное: написанное в нём — то, что сейчас разбирается, и допечатанное
   * после ответа стёрлось бы. Бледным поле остаётся только у выключенной задачи.
   */
  busy?: boolean
  /** Имя поля для программы чтения с экрана: плейсхолдер ей не заголовок. */
  label?: string
  /**
   * Сдвиг числа — вернуть фокус в поле. Нажатая кнопка, исчезнув с экрана,
   * роняет фокус на страницу («Спросить другое» под карточками, SMY-487).
   */
  focusKey?: number
}) {
  const picker = useRef<HTMLInputElement>(null)
  const field = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (focusKey != null && focusKey > 0) field.current?.focus()
  }, [focusKey])

  // Высота — по тексту, на каждой правке: сброс до `auto`, чтобы стёртые строки
  // отдавали место, потом высота содержимого вместе с рамкой. До потолка;
  // дальше поле прокручивается (`maxHeight` ниже).
  useLayoutEffect(() => {
    const el = field.current
    if (el == null) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`
  }, [value])

  return (
    <div className="composer">
      <div className={`composer__box${onAttach != null ? ' composer__box--attach' : ''}`}>
        {onAttach != null ? (
          <>
            <button
              type="button"
              className="composer__attach"
              aria-label="Прикрепить файл"
              disabled={disabled === true || busy === true}
              onClick={() => picker.current?.click()}
            >
              <Icon name="attach" size={22} />
            </button>
            <input
              ref={picker}
              type="file"
              hidden
              // Список форматов — из ядра, а не строкой здесь: он же проверяется
              // на сервере, и разойтись этим двум местам нельзя.
              accept={ACCEPT}
              onChange={(e) => {
                const file = e.target.files?.[0]
                // Значение сбрасывается сразу: иначе тот же файл, выбранный
                // повторно после ошибки, не вызовет события вовсе.
                e.target.value = ''
                if (file != null) onAttach(file)
              }}
            />
          </>
        ) : null}

        <textarea
          ref={field}
          rows={1}
          className={`field composer__field${value !== '' ? ' field--active' : ''}`}
          style={{ maxHeight: COMPOSER_MAX_HEIGHT }}
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={busy === true}
          {...(label != null ? { 'aria-label': label } : {})}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            // Enter отправляет, ⇧Enter переносит строку — подсказки под полем
            // на W02 обещают именно это. Enter, которым набор через IME
            // подтверждает слово, — не отправка: иначе вопрос уходил бы
            // недописанным посреди иероглифа.
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing && busy !== true && onSend) {
              e.preventDefault()
              // Закрытая отправка: Enter ничего не делает, как при пустом поле, —
              // перенос строки на месте ожидаемой отправки читался бы сбоем.
              if (sendBlocked !== true) onSend()
            }
          }}
        />
      </div>
      {onSend ? <SendButton onClick={onSend} disabled={disabled || sendBlocked === true || value.trim() === ''} busy={busy === true} /> : null}
    </div>
  )
}


export interface ToggleProps {
  checked: boolean
  onChange?: (checked: boolean) => void
  disabled?: boolean
  label: string
}

/**
 * Тогл. Размеры веба — 46×28, ручка 24 (экран W10). Мобильные 51×31 лежат в
 * MOBILE и достаются в P9: подставлять сюда числа платформы, которой ещё
 * нет, значит держать в вебе размер, которого он не просил.
 */
export function Toggle({ checked, onChange, disabled, label }: ToggleProps) {
  const { width, height, knob } = WEB.toggle
  const inset = (height - knob) / 2

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      className="toggle"
      style={{ width, height }}
      onClick={() => onChange?.(!checked)}
    >
      <span
        className="toggle__knob"
        style={{ width: knob, height: knob, left: checked ? width - knob - inset : inset }}
      />
    </button>
  )
}

export function Checkbox({
  checked,
  onChange,
  children,
}: {
  checked: boolean
  onChange: (checked: boolean) => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      className="checkbox"
      onClick={() => onChange(!checked)}
    >
      <span className="checkbox__box">{checked ? <Icon name="tick" size={13} strokeWidth={2.6} /> : null}</span>
      <span>{children}</span>
    </button>
  )
}
